import { observable, computed, action } from 'mobx';
import { app } from './../App';
import { IUser, IUserCustomer, ICustomerAddress, CustomerType, EUserTypeName } from './../../../common/interfaces/user';

interface IAuthentication {
    accessToken: string;
}

interface IAuthenticationUser extends IAuthentication {
    user: IUser;
}

interface ITokenPayload {
    accessToken: string;
    aud: string;
    exp: number;
    iat: number;
    iss: string;
    sub: string;
    userId: number;
}

// Store
export class User {
    constructor() {}

    public errorUserDoesNotExistMessage = 'User does not exist';
    public errorTokenIsInvalidMessage = 'Token is invalid';
    public errorNoAccessTokenReturned = 'No access token returned';
    public errors = [this.errorUserDoesNotExistMessage, this.errorTokenIsInvalidMessage, this.errorNoAccessTokenReturned];

    // User info
    @observable public id: number | null = null;
    @observable public username: string | null = null;
    @observable public userType: EUserTypeName | null = null;
    @observable public email: string | null = null;
    @observable public permissionId: number = 1;
    @observable public customer: IUserCustomer = null;

    @observable public showUserDropdown: boolean = false;

    @computed
    public get isLoggedIn(): boolean {
        return this.id !== null && this.username !== null && this.userType !== null ? true : false;
    }

    // Check if user belongs to customer company
    @computed
    public get isCustomer(): boolean {
        return this.isLoggedIn && this.customer !== null;
    }

    // Check user customer group if it's a customer
    @computed
    public get customerType(): CustomerType | null {
        if (
            this.isLoggedIn &&
            this.isCustomer &&
            typeof this.customer !== 'undefined' &&
            typeof this.customer.custgroup !== 'undefined'
        ) {
            return this.customer.custgroup !== null ? this.customer.custgroup : null;
        } else {
            return null;
        }
    }

    // Check if user's customer group is PAC
    @computed
    public get customerIsPAC(): boolean {
        return this.customerType === 'PAC';
    }

    // Check if user's customer group is PBI or derivative
    @computed
    public get customerIsPBI(): boolean {
        return this.customerType === 'PBI' || this.customerType === 'PBI+';
    }

    @computed
    public get customerIsPBIRegular(): boolean {
        return this.customerIsPBI && this.customerType === 'PBI';
    }

    @computed
    public get customerIsPBIPlus(): boolean {
        return this.customerIsPBI && (this.customerType === 'PBI+' || this.customer.splgroup === 1);
    }

    @computed
    public get customerIsOnPriceListNumSeven(): boolean {
        return this.customer && this.customer.listnum && this.customer.listnum === 7;
    }

    // Customer addresses
    @computed
    public get customerAddresses(): { billing: ICustomerAddress[]; shipping: ICustomerAddress[] } {
        const r = {
            billing: [],
            shipping: []
        };

        if (this.isCustomer && typeof this.customer.address !== 'undefined' && this.customer.address) {
            if (typeof this.customer.address.billing !== 'undefined' && this.customer.address.billing) {
                r.billing = this.customer.address.billing.map((addr) => addr);
            }

            if (typeof this.customer.address.shipping !== 'undefined' && this.customer.address.shipping) {
                r.shipping = this.customer.address.shipping.map((addr) => addr);
            }

            return r;
        } else {
            return r;
        }
    }

    // Customer's default shipping address
    @computed
    public get customerDefaultShippingAddress(): ICustomerAddress | null {
        if (this.customerAddresses.shipping.length > 0 || this.customerAddresses.billing.length > 0) {
            let defaultShippingAddress: ICustomerAddress | null = null;
            if (this.customerAddresses.shipping.length > 0) {
                this.customerAddresses.shipping.some((addr) => {
                    if (addr.isDefault) {
                        defaultShippingAddress = addr;
                        return true;
                    }
                });
            }

            if (defaultShippingAddress === null && this.customerAddresses.billing.length > 0) {
                this.customerAddresses.billing.some((addr) => {
                    if (addr.isDefault) {
                        defaultShippingAddress = addr;
                        return true;
                    }
                });
            }

            if (defaultShippingAddress === null) {
                defaultShippingAddress =
                    this.customerAddresses.shipping.length > 0
                        ? this.customerAddresses.shipping[0]
                        : this.customerAddresses.billing.length > 0
                        ? this.customerAddresses.billing[0]
                        : null;
            }

            return defaultShippingAddress;
        } else {
            return null;
        }
    }

    // Customer's payment terms
    @computed
    public get customerPaymentTerms(): string | null {
        return this.isCustomer && this.customer.terms !== null ? this.customer.terms : null;
    }

    // Customer's default warehouse
    @computed
    public get customerDefaultWarehouseCode(): string | null {
        return this.isCustomer && this.customer.whscode !== null ? this.customer.whscode : null;
    }

    // Get user's company's list number
    @computed
    public get customerListNumber(): number | null {
        return this.isLoggedIn && this.isCustomer && typeof this.customer.listnum !== 'undefined'
            ? this.customer.listnum
            : null;
    }

    // Save user info
    @action
    private saveUserData = async (user: IUser) => {
        const userTypeNameKeys = Object.keys(EUserTypeName);

        app.set('user', user);

        this.id = user.id;
        this.username = user.username;
        this.email = user.email;
        this.permissionId = user.permissionId || 1;
        this.customer = typeof user.customer !== 'undefined' ? user.customer : null;
        this.userType =
            typeof userTypeNameKeys[user.typeId] !== 'undefined'
                ? (userTypeNameKeys[user.typeId] as EUserTypeName)
                : EUserTypeName.None;
    };

    // Get customer data
    @action
    private fetchCustomer = async (cardcode: string): Promise<IUserCustomer> => {
        try {
            const customerService = await app.service('customer');
            const customer = (await customerService.get(cardcode)) as IUserCustomer;

            return customer;
        } catch (err) {
            console.error(err);
            return null;
        }
    };

    // Relogin via local token
    @action
    public relogin = async (): Promise<IUser> => {
        const errorNoValidTokenMessage = 'No valid token stored to re-login';

        try {
            const token: string = await app.passport.getJWT();

            if (token) {
                const payload: IAuthenticationUser = await app.authenticate(undefined);
                if (typeof payload !== 'undefined' && payload !== null && typeof payload.user !== 'undefined') {
                    let user = payload.user;

                    if (user && typeof user.cardcode !== 'undefined' && user.cardcode) {
                        const customer = await this.fetchCustomer(user.cardcode);

                        if (customer) {
                            user = { ...user, customer };
                        }
                    }

                    await this.saveUserData(user);

                    return user;
                } else {
                    throw new Error('Token is invalid');
                }
            } else {
                throw new Error(errorNoValidTokenMessage);
            }
        } catch (err) {
            if (err === errorNoValidTokenMessage) {
                console.warn(err);
            } else {
                console.error(err);
            }
            return err;
        }
    };

    // Login
    @action
    public login = async (username: string, password: string): Promise<IUser> => {
        try {
            const response: IAuthentication = await app.authenticate({
                strategy: 'local',
                username: username.trim(),
                password: password.trim()
            });

            if (typeof response.accessToken !== 'undefined' && response.accessToken !== null) {
                const payload: ITokenPayload = await app.passport.verifyJWT(response.accessToken);

                if (typeof payload !== 'undefined' && payload !== null && typeof payload.userId !== 'undefined') {
                    let user: IUser = (await app.service('users').get(payload.userId)) as IUser;

                    if (typeof user !== 'undefined' && typeof user.createdAt !== 'undefined') {
                        if (user && typeof user.cardcode !== 'undefined' && user.cardcode) {
                            const customer = await this.fetchCustomer(user.cardcode);

                            if (customer) {
                                user = { ...user, customer };
                            }
                        }

                        await this.saveUserData(user);

                        return user;
                    } else {
                        throw new Error(this.errorUserDoesNotExistMessage);
                    }
                } else {
                    throw new Error(this.errorTokenIsInvalidMessage);
                }
            } else {
                throw new Error(this.errorNoAccessTokenReturned);
            }
        } catch (err) {
            if (this.errors.indexOf(err) !== -1) {
                console.warn(err);
            } else {
                console.error(err);
            }
            return err;
        }
    };

    // Logout
    @action
    public logout = async (): Promise<any> => {
        try {
            const loggedOut = await app.logout();

            this.id = null;
            this.username = null;
            this.userType = null;
            this.permissionId = 1;
            this.customer = null;

            return true;
        } catch (err) {
            console.error(err);
            return err;
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/User.ts